import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BehaviorSubject, merge } from 'rxjs';
import { useBSubject } from 'src/hooks/useBSubject';
import { distinctUntilChanged, skip, tap } from 'rxjs';
import classnames from 'classnames';
import classes from 'src/components/Algolia/SearchDropdown/Search.scss';
import { HeaderContext } from 'src/components/Header/Header';
import { useClickOutside } from 'src/hooks/useClickOutside';

import { CustomSearchForm } from './SearchForm';
import { SearchPlaceholder } from './SearchPlaceholder';

export const DropdownLoaderLoader: React.FC<{ trigger: SearchTrigger }> = React.lazy(
    () => import(/* webpackChunkName:"__lazy__algolia-instant-search" */ './DropdownLoader'),
);

export type SearchTrigger = BehaviorSubject<string>;

/**
 * Don't open the dropdown (and so don't query Algolia) until the term is
 * long enough to be useful — 1-2 characters match most of the catalogue.
 */
export const MIN_QUERY_LENGTH = 3;
export const isSearchable = (value: string) => value.trim().length >= MIN_QUERY_LENGTH;

export const MOBILE_SEARCH_INPUT_ID = 'search-mobile';

export const SearchLoader: React.FC<{ inputId?: string }> = React.memo(({ inputId }) => {
    const { showSearch, setShowSearch } = useContext(HeaderContext);

    const outerRef = useRef<HTMLDivElement | null>(null);

    const close = useCallback(() => setShowSearch(false), [setShowSearch]);

    useClickOutside(outerRef, showSearch, close);

    const sub = useBSubject<string>('');

    return (
        <div
            data-scrolllock-search
            ref={outerRef}
            className={classnames(classes.Search, {
                [classes.SearchActive]: showSearch,
            })}
        >
            <CustomSearchForm trigger={sub} inputId={inputId} />
            <Wrapper trigger={sub} />
        </div>
    );
});

export const DropdownContext = React.createContext<{ setOpen?(open: boolean) }>({});

const Wrapper = React.memo((props: { trigger: SearchTrigger }) => {
    const { showNav } = useContext(HeaderContext);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const sub = merge(props.trigger)
            .pipe(
                skip(1),
                distinctUntilChanged(),
                tap((searchValue) => {
                    setOpen(isSearchable(searchValue));
                }),
            )
            .subscribe();
        return () => {
            sub.unsubscribe();
        };
    }, [props.trigger]);

    if (open && !showNav) {
        return (
            <React.Suspense fallback={<SearchPlaceholder />}>
                <DropdownContext.Provider value={{ setOpen }}>
                    <DropdownLoaderLoader trigger={props.trigger} />
                </DropdownContext.Provider>
            </React.Suspense>
        );
    }

    return null;
});
